import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Select, TreeSelect, message } from "antd";
import { FaBroom } from "react-icons/fa";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";

import "./styles.scss";
import "../styles.scss";
import { filterFolders, formatChatReference } from "..";
import ChatCard, { ChatLoadingCard } from "../chart-card";
import FileSaver from "../../file-saver";
import SystemPrompts from "../../system-prompts";
import Breadcrumbs from "../../../../assets/component/breadcrumbs";
import PDFPreview from "../../../../assets/component/pdf-preview";
import ChatInput from "../../../../assets/component/chat-input";
import { scrollToBottom, scrollToNewChat } from "../../../../helpers/chat-scrollers";
import { findNodeByValue, getAllDescendantNodes } from "../../../../helpers/tree-select";
import { GetFolderStructureAction, GetFolderStructureReset } from "../../../../store/slices/custom-research/folder-structure";
import { ESGChatAction, ESGChatFeedbackAction, ESGChatFeedbackReset, ESGChatReset } from "../../../../store/slices/ai-chat/esgChat";

// const defaultOption = { label: "All", title: "All", value: "All" };
const presetSystemPrompt = "You are a question answering agent. I will provide you with a set of search results. The user will provide you with a question. Your job is to answer the user's question using only information from the search results. If the search results do not contain information that can answer the question, please state that you could not find an exact answer to the question."

const modelOptions = [
    { label: "Model A (Advanced)", value: "Model A" },
    { label: "Model B (Basic)", value: "Model B" },
]

const defaultSelectedModel = ["Model A", "Model B"]

const ModelComparisonChat = () => {
    const [chatData, setChatData] = useState([]);
    const [questionInput, setQuestionInput] = useState("");
    const [chatFoldersList, setChatFoldersList] = useState([]);
    const [showAddonSection, setShowAddonSection] = useState(true);
    const [selectedFolders, setSelectedFolders] = useState([]);
    const [selectedModels, setSelectedModels] = useState([]);
    const [systemPromptsList, setSystemPromptsList] = useState([]);
    const [showPDFPreview, setShowPDFPreview] = useState({
        show: false,
        isPdf: true,
    });

    const scrollRef = useRef();
    const dispatch = useDispatch()
    const { data: chatResponse, isLoading: loadingStatus, error: chatResponseError } = useSelector((state) => state.esgChat);
    const { data: uploadFolders } = useSelector((state) => state.fetchFolders);
    const { data: chatFeedback, success: chatFeedbackSuccess, error: chatFeedbackError } = useSelector((state) => state.chatFeedback);

    const responseFormatter = ({ res, question }) => {
        // Formatting response
        if (res) {
            setTimeout(() => {
                scrollToNewChat({ scrollRef: scrollRef });
            }, 500);

            // Refactor Highlight Data
            const currentReferences = res?.citations
                ?.map((item) => {
                    const listData = item?.retrievedReferences?.map((data) => {
                        return formatChatReference({
                            data: data,
                            searchType: "Custom-Comparison",
                        });
                    });
                    return listData;
                })
                .flat();

            const updatedRes = {
                ...res,
                question: question,
                sources: currentReferences,
            };
            return updatedRes
        }
    };

    const handleNewResponse = ({ status, responseData, feedback }) => {
        if (status === "Loading") {
            const formattedChat = {
                question: questionInput,
                models: selectedModels?.map((model) => ({ key: model, status: "Loading", question: questionInput })),
                feedback: {
                    available: false,
                    type: ""
                }
            }

            setChatData([...chatData, formattedChat])
            scrollToBottom({ scrollRef: scrollRef });
        }

        if ((status === "Success") && responseData) {
            setChatData((prevChatData) => {
                if (prevChatData.length === 0) {
                    return prevChatData;
                }

                const lastChat = prevChatData[prevChatData.length - 1];
                const formattedResponse = responseFormatter({ res: responseData.result, question: lastChat?.question })

                const updatedModels = lastChat.models.map((model) =>
                    model.key === responseData.model
                        ? { ...model, ...formattedResponse, status }
                        : model
                );

                return [...prevChatData.slice(0, -1), { ...lastChat, models: updatedModels }];
            });

            scrollToBottom({ scrollRef: scrollRef });
        }

        if ((status === "Feedback") && feedback) {
            setChatData((prevChatData) => {
                if (prevChatData.length === 0) {
                    return prevChatData;
                }

                const lastChat = prevChatData[prevChatData.length - 1];

                const updatedModels = lastChat?.models?.map((model) =>
                    model.key === feedback?.key
                        ? { ...model, ...feedback }
                        : model
                );

                return [...prevChatData.slice(0, -1), { ...lastChat, models: updatedModels }];
            });
        }
    }

    const handleExpandCollapse = () => {
        setShowAddonSection(!showAddonSection);
    };

    const handlePDFPreview = ({ url }) => {
        setShowPDFPreview({ show: true, isPdf: url?.includes(".pdf") });
    };

    const handleFeedback = (e, data) => {
        const payload = {
            userFeedback: e,
            key: data?.key,
            feedbackId: data?.feedbackId,
        };

        const feedData = {
            ...payload,
            status: "Success",
            feedStatus: "FEEDBACK-LOADING",
        }

        handleNewResponse({ status: "Feedback", responseData: {}, feedback: feedData })
        dispatch(ESGChatFeedbackAction({ payload: payload, id: data?.feedbackId }));
    };

    const handleSelectFolders = (selectedValues) => {
        const maxFoldersToSelect = 5;
        const hasAllSelected = selectedValues.some((item) => item.value === "All");

        if (hasAllSelected) {
            setSelectedFolders([{ label: "All", value: "All" }]);
        } else if (selectedValues.length > maxFoldersToSelect) {
            message.error(`Maximum limit to select is ${maxFoldersToSelect}`);
        } else {
            const newSelection = selectedValues.find(
                (item) => !selectedFolders.some((folder) => folder.value === item.value)
            );

            if (newSelection) {
                const selectedNode = findNodeByValue(
                    newSelection.value,
                    chatFoldersList
                );
                let nodesToAdd = [
                    { label: selectedNode.title, value: selectedNode.value },
                ];

                if (selectedNode.children?.length) {
                    nodesToAdd = [...nodesToAdd, ...getAllDescendantNodes(selectedNode)];
                }

                const allSelectedNodes = [...selectedFolders, ...nodesToAdd];
                const uniqueSelectedNodes = Array.from(
                    new Map(allSelectedNodes.map((item) => [item.value, item])).values()
                );

                setSelectedFolders(uniqueSelectedNodes);
            } else {
                const missingFolders = selectedFolders.filter(
                    (folder) =>
                        !selectedValues.some((selected) => selected.value === folder.value)
                );

                if (missingFolders.length) {
                    const missingFolderValue = missingFolders[0].value;

                    const updatedSelectedValues = selectedValues.filter(
                        (selected) => !selected.value.includes(missingFolderValue)
                    );

                    setSelectedFolders(updatedSelectedValues);
                } else {
                    setSelectedFolders(selectedValues);
                }
            }
        }
    };

    const handleSelectModels = (e) => {
        const formattedModels = e?.map((model) => ({ title: model, systemPrompt: presetSystemPrompt })) || []

        setSystemPromptsList(formattedModels)
        setSelectedModels(e)
    }

    const handleClearChat = () => {
        setChatData([]);
    };

    const handleSubmit = () => {
        const foldersAvailable = (selectedFolders?.length > 0)
        const modelsAvailable = (selectedModels?.length > 0)

        if (foldersAvailable && modelsAvailable) {
            const formattedFiles = [];
            const formattedFolders = [];
            const isTreeAllSelected = selectedFolders[0]?.value.includes("All");

            selectedFolders?.forEach((item) => {
                if (item?.value?.includes(".pdf")) {
                    formattedFiles.push(item);
                } else {
                    formattedFolders.push(item?.value);
                }
            });

            let payload = {
                question: questionInput,
                year: [],
                searchType: "Custom",
                searchBy: "Private",
                sessionId: "",
                customFolder: "",
                companyName: [],
                isUpload: true,
                isLabs: true,
                fileArray: isTreeAllSelected ? [] : formattedFiles,
                folderArray: isTreeAllSelected ? [] : formattedFolders,
            }

            selectedModels?.map((model) => {
                dispatch(ESGChatReset())

                const currentModelPrompt = systemPromptsList?.find((item) => item?.title === model)
                payload = {
                    ...payload,
                    model: model,
                    systemPrompt: currentModelPrompt?.value || presetSystemPrompt
                }

                dispatch(ESGChatAction(payload))
            })
        }

        if (!questionInput) {
            message?.error("Please add a question!")
        } else if (!foldersAvailable) {
            message?.error("Please select a folder!")
        } else if (!modelsAvailable) {
            message?.error("Please select a model!")
        }
    }

    // Initial Call
    useEffect(() => {
        // Reset
        dispatch(GetFolderStructureReset());

        // API & Functions Calls
        handleSelectModels(defaultSelectedModel)
    }, [])

    useEffect(() => {
        if (chatResponse) {
            handleNewResponse({ status: "Success", responseData: chatResponse, feedback: {} })

            setQuestionInput("")
        }
    }, [chatResponse]);

    useEffect(() => {
        if (chatResponseError?.message) {
            const formattedChatData = {
                question: questionInput,
                answer:
                    "Failed to get response due to some Technical issue. Please try again",
            };

            const newChatData = [...chatData, formattedChatData];

            setQuestionInput("")
            setChatData(newChatData);
            dispatch(ESGChatReset());
        }
    }, [chatResponseError?.message]);

    useEffect(() => {
        if (uploadFolders?.files) {
            const filteredFolders = filterFolders(uploadFolders.files);

            if (uploadFolders?.files?.length > 0) {
                // setChatFoldersList([defaultOption, ...filteredFolders]);
                setChatFoldersList([...filteredFolders]);
            } else {
                setChatFoldersList(filteredFolders);
            }
        }
    }, [uploadFolders?.files]);

    useEffect(() => {
        if (questionInput && loadingStatus) {
            handleNewResponse({ status: "Loading", responseData: chatResponse, feedback: {} })
        }
    }, [loadingStatus]);

    useEffect(() => {
        if (chatFeedbackSuccess) {
            message.success(chatFeedback?.message)

            setChatData((prevChatData) => {
                return prevChatData.map((chat) => {
                    const updatedModels = chat.models.map((model) =>
                        model.feedbackId === chatFeedback?.feedbackId
                            ? { ...model, feedStatus: "FEEDBACK-SUCCESS" }
                            : model
                    );

                    return { ...chat, models: updatedModels };
                });
            });

            dispatch(ESGChatFeedbackReset());
            dispatch(ESGChatReset());
        }
    }, [chatFeedbackSuccess, chatFeedback]);

    useEffect(() => {
        if (chatFeedbackError?.message) {
            message?.error(chatFeedbackError?.message)

            dispatch(ESGChatFeedbackReset());
        }
    }, [chatFeedbackError?.message])

    return (
        <div className="esg-chat-container">
            {/* Right section */}
            <div className="chat-right-section">
                <div className="expand-collapse-section-container">
                    <div className="button-wrapper expand-collapse-button" onClick={() => handleExpandCollapse()}>
                        {showAddonSection ? <FaChevronRight /> : <FaChevronLeft />}
                    </div>
                </div>
                {showAddonSection && (
                    <div className="addon-section">
                        <div className="addon-section-container">
                            <SystemPrompts setSystemPrompts={setSystemPromptsList} modelsList={systemPromptsList} />
                            <div className="break-line" />
                            <FileSaver
                                showCreateFolder={false}
                                sectionTitle="My Files"
                                activeTab={"Completed"}
                                requiredFields={["FILE"]}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Left Section */}
            <div className="contents">
                <Breadcrumbs />

                {/* Filters */}
                <div className="filter-section-wrapper">
                    <div className="filters-section">
                        {/* Folders */}
                        <div className="input-wrapper">
                            <div className="input-label">
                                Select Folder
                                <span>*</span>
                            </div>
                            <TreeSelect
                                className="input-main"
                                popupClassName="chat-react-select"
                                multiple
                                treeCheckable
                                treeCheckStrictly
                                showCheckedStrategy="SHOW_PARENT"
                                treeData={chatFoldersList}
                                value={selectedFolders}
                                onChange={handleSelectFolders}
                                placeholder="Select Folder"
                                maxTagCount={1}
                                filterTreeNode={(input, option) => {
                                    return (
                                        option?.key
                                            ?.toLowerCase()
                                            ?.indexOf(input?.toLowerCase()) >= 0 ||
                                        option?.title
                                            ?.toLowerCase()
                                            ?.indexOf(input?.toLowerCase()) >= 0
                                    );
                                }}
                            />
                        </div>
                        {/* Models */}
                        <div className="input-wrapper">
                            <div className="input-label">
                                Select Models
                                <span>*</span>
                            </div>
                            <Select
                                showSearch
                                allowClear
                                className="input-main"
                                popupClassName="chat-react-select"
                                mode="multiple"
                                options={modelOptions}
                                onChange={handleSelectModels}
                                value={selectedModels}
                                placeholder="Select Models"
                                maxLength={2}
                                filterOption={(input, option) =>
                                    option?.label
                                        ?.toLowerCase()
                                        ?.includes(input?.toLowerCase())
                                }
                            />
                        </div>
                    </div>
                </div>

                {/* Chat Section */}
                <div className="chat-container included-filters">
                    <div className="chat-contents-section" ref={scrollRef}>
                        <div className="chat-wrap">
                            {chatData?.map((data, index) => {
                                return (
                                    <>
                                        <div className="comparision-chat-section-wrapper" key={index}>
                                            {data?.models?.map((model) => {
                                                return (
                                                    <div className="comparison-chat-card-wrapper" key={model?.key}>
                                                        <div className="model-name">{model?.key}</div>

                                                        {(model?.status === "Loading") && (
                                                            <ChatLoadingCard key={model?.key} question={model?.question} />
                                                        )}

                                                        {(model?.status === "Success") && (
                                                            <ChatCard
                                                                key={model.key}
                                                                data={model}
                                                                chatIndex={index}
                                                                setQuestion={setQuestionInput}
                                                                handleFeedback={handleFeedback}
                                                                handlePDFPreview={handlePDFPreview}
                                                                feedbackSuccess={model?.feedStatus === "FEEDBACK-SUCCESS"}
                                                                feedbackLoading={model?.feedStatus === "FEEDBACK-LOADING"}
                                                                showFeedback={!model?.userFeedback}
                                                                showUploadSection={true}
                                                            />
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </>
                                );
                            })}
                        </div>
                    </div>

                    {/* Clear Chat icon */}
                    {(chatData?.length > 0) && (
                        <div className="clear-chat-wrapper" onClick={() => handleClearChat()}>
                            <FaBroom /> Clear
                        </div>
                    )}

                    {/* Chat Input */}
                    <ChatInput
                        chatLoading={loadingStatus}
                        handleSubmit={handleSubmit}
                        questionInput={questionInput}
                        setQuestionInput={setQuestionInput}
                    />
                </div>
            </div>

            {/* PDF Preview */}
            {showPDFPreview?.show && (
                <PDFPreview
                    setShow={setShowPDFPreview}
                    isDocDownload={!showPDFPreview?.isPdf}
                />
            )}
        </div>
    )
}

export default ModelComparisonChat